/** @module SiteAdmin/Locale/util */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/date/locale",
	"csi/def",
	"csi/def/Action",
	"csi/html",
	"csi/locale",
	"csi/types",
	"csi/xml"
], function (declare, lang, dojoLocale, def, Action, html, locale, types, xml) {
	/** @property {Date} DATE_FOR_TEST - only used to test formatting and date/time value */
	var DATE_FOR_TEST = new Date(2009, 3, 26, 13, 37, 43, 777);

	/**
	 * Return the locale setting based on the current context
	 * @param {Context} ctx
	 * @return {string}
	 */
	function getLocaleSettings(ctx) {
		var locale = ctx.getNode();
		return ctx.cache.getNode(locale.SetupSettings);
	}

	/**
	 * Return the example format based on the current locale with the specified selector and pattern
	 * @param {Node} localeNode
	 * @param {string} selector - either "date" or "time"
	 * @param {string} pattern - if specified, then it'll be applied; otherwise, the default will be applied
	 * @param {Date} date - if specified, then it'll be applied; otherwise, the current time will be applied
	 * @return {string}
	 */
	function getDateTimeFormatBasedOnLocale(localeNode, selector, pattern, date) {
		var options;

		if (pattern) {
			options = {
				// see #5465 - always format with 4-digit years
				fullYear: true,
				locale: localeNode.$Name,
				selector: selector,
				datePattern: pattern,
				timePattern: pattern
			}
		} else {
			options = {
				// see #5465 - always format with 4-digit years
				fullYear: true,
				locale: localeNode.$Name,
				selector: selector
			}
		}

		return dojoLocale.format(date || DATE_FOR_TEST, options);
	}

	/**
	 * Return true if the current pattern is the default based on the current locale
	 * @param {Node} localeNode
	 * @param {string} selector - either "date" or "time"
	 * @param {string} pattern - if specified, then it'll be applied; otherwise, the default will be applied
	 * @return {boolean}
	 */
	function isDefaultPattern(localeNode, selector, pattern) {
		var time = new Date();
		return getDateTimeFormatBasedOnLocale(localeNode, selector, pattern, time) == getDateTimeFormatBasedOnLocale(localeNode, selector, "", time);
	}

	/**
	 * Get the CSS for the date/time attribute based on the status
	 * @param {Context} ctx
	 * @param {string} selector - will get CSS for Date if specify with "date", "time" for Time
	 * @param {string} css - original CSS
	 * @return {string}
	 */
	function getDateTimeRenderCss(ctx, selector, css) {
		var node = ctx.getNode();
		var locale = ctx.getNodeParent();
		var setupSettings = ctx.getNode(locale.SetupSettings);
		var pattern = (selector == "date") ? node.DatePattern : node.TimePattern;

		var bDefault = isDefaultPattern(locale, selector, pattern);

		if (pattern && bDefault && !setupSettings.Modified) {
			css += " " + "csi-bg-changed";
		}

		return css;
	}

	/**
	 * Add the tooltip for the date/time attribute as needed
	 * @param {Context} ctx
	 * @param {Tooltip} tooltip
	 * @param {string} pattern - Date/Time pattern
	 * @param {string} selector - will add appropriate tooltip for Date if specify with "date", "time" for Time
	 */
	function addTooltipAsNeeded(ctx, tooltip, pattern, selector) {
		var bDefault = isDefaultPattern(ctx.getNodeParent(), selector, pattern);

		if (!pattern || bDefault) {
			return null;
		}

		var action = lang.mixin(new Action(), {
			m_id: "ResetToDefault",
			m_typeControl: def.ACTION_LINK,
			m_onAction: function (ac) {
				const localeName = ac.getParent().$Name;
				const value = types.getDefaultDateTimeFormat(localeName, selector == "date" ? types.FORMAT_DATE_SHORT : types.FORMAT_TIME_SHORT);
				ac.m_writer.changeNode(ac.m_urlNode).changeAttribute(selector == "date" ? "DatePattern" : "TimePattern", xml.ATTR_STRING, value);
			},
			m_postAction: "postActionRedrawNode"
		});

		action.resolve();
		tooltip.addAction(action);

		var content = locale.resource("DefaultValue") + ":" + "\n";
		var contentDiv = html.create("div", {
			innerText: content,
			style: 'font-weight:bold;'
		});

		tooltip.addContent(contentDiv);

		// get the default format based on the Dojo locale
		var settings = getLocaleSettings(ctx);
		var currentLocale = ctx.getParent(settings);
		var defaultValue = getDateTimeFormatBasedOnLocale(currentLocale, selector, "");

		tooltip.addContent(html.create("div", { innerText: defaultValue }));
	}

	return {
		addTooltipAsNeeded: addTooltipAsNeeded,
		getLocaleSettings: getLocaleSettings,
		getDateTimeFormatBasedOnLocale: getDateTimeFormatBasedOnLocale,
		isDefaultPattern: isDefaultPattern,
		getDateTimeRenderCss: getDateTimeRenderCss
	};
});